// React hook that loads the three catalog collections + cabinets for a
// project and returns a memoised ProjectBom. Recomputes whenever any
// cabinet row or catalog entry changes (cheap — pure functions over
// in-memory arrays).

import { useMemo } from "react";

import { useCollection, useCabinetsIn } from "@/lib/ddb-hooks";
import type { EdgeBand, Hardware, Id, Material } from "@/lib/types";
import { indexCatalogs, projectBom, type ProjectBom } from "./bom";

export interface UseBomState {
  bom: ProjectBom | null;
  isLoading: boolean;
  error: Error | null;
}

export function useBom(projectId: Id | null | undefined): UseBomState {
  const m = useCollection<Material>("materials");
  const e = useCollection<EdgeBand>("edge_bands");
  const h = useCollection<Hardware>("hardware");
  const c = useCabinetsIn(projectId);

  const cat = useMemo(() => {
    if (!m.data || !e.data || !h.data) return null;
    return indexCatalogs(m.data, e.data, h.data);
  }, [m.data, e.data, h.data]);

  const bom = useMemo(() => {
    if (!cat || !c.data) return null;
    return projectBom(c.data, cat);
  }, [cat, c.data]);

  const isLoading = m.isLoading || e.isLoading || h.isLoading || c.isLoading;
  const error =
    (m.error as Error | null) ??
    (e.error as Error | null) ??
    (h.error as Error | null) ??
    (c.error as Error | null) ??
    null;

  return { bom, isLoading, error };
}
